import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import {
  Container, Box, Grid, Typography, Button, CircularProgress, Link, IconButton,
  MenuItem,
  Menu,
  Paper,
  Tooltip,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { ArrowBack, Mail } from "@mui/icons-material";

import { getRequestFiled, resendEmail, getFiled } from "../../services/backend/CorrespondenceService";
import FiledViewItem from "./FiledViewItem";
import { PERMISSIONS } from "../../constants";
import DialogConfirmation from "../generic/DialogConfirmation";
import RadicadoTimeline from "./RadicadoTimeline";
import RadicadoBacklink from "./RadicadoBacklink";
import CancelFiledDialog from "./CancelFiledDialog";
import FiledCancellationNotice from "./FiledCancellationNotice";
import { canCancelFiled } from "./cancelPermission";
import useFiledConfig from "./useFiledConfig";

export default function FiledView() {
  const { hash } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [request, setRequest] = useState();
  // Why the server would refuse to annul it; only sent to a signed-in user.
  const [cancelRefusal, setCancelRefusal] = useState(null);
  // Who annulling would email; staff only, like cancelRefusal.
  const [cancelNotice, setCancelNotice] = useState(null);
  const [confirmInfo, setConfirmInfo] = useState({ open: false });
  const [anchorEl, setAnchorEl] = useState(null);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const openMenu = Boolean(anchorEl);
  const filedConfig = useFiledConfig(user);

  useEffect(() => {
    (async () => {
      let responseJSON = await getRequestFiled(user?.token, hash);

      if (!responseJSON?.request) {
        navigate("/filedlist");
        return;
      }
      setRequest(responseJSON.request);
      setCancelRefusal(responseJSON.cancelRefusal || null);
      setCancelNotice(responseJSON.cancelNotice || null);
    })();
  }, [user, hash, navigate]);

  const handleAddResponse = () => {
    if (request?.filedId) {
      navigate(`/filed?responseMode=true&parentRadicadoId=${request.filedId}`);
    }
  };

  const isCancelled = request?.status === "CANCELLED";
  // The archiver switch in config/filed can take this away from the role.
  const cancelAccess = canCancelFiled(user, filedConfig);
  const canCancel = request?.status === "DONE" && cancelAccess.visible;
  const cancelDisabledReason = cancelRefusal || cancelAccess.reason;

  const canAddResponse = request &&
    request.status === "DONE" &&
    !request.response?.parentRadicadoId &&
    request.response?.respondedorUserId &&
    (request.response.respondedorUserId === user?.userUID || 
     request.response.respondedorUserId === user?.userId);

  return (
    <>
      <DialogConfirmation
        confirmInfo={confirmInfo}
        handleCloseDialog={(result) => {
          if (result) {
            const req = {...request};
            req.carbonCopy = [];
            if (confirmInfo.anchor === "senders") req.recipients = [];
            if (confirmInfo.anchor === "recipients") req.senders = [];
            resendEmail(user.token, req);
          }
          setConfirmInfo({ open: false });
          setAnchorEl(null);
        }}
      />
      <CancelFiledDialog
        open={openCancelDialog}
        filedId={request?.filedId}
        finalized
        notice={cancelNotice}
        onClose={() => setOpenCancelDialog(false)}
        onCancelled={() => {
          setOpenCancelDialog(false);
          navigate(`/filed/cancelled/${request.filedId}`, { replace: true });
        }}
      />
      {/* Signed out, this page was opened from a QR label or an email link:
          there is nothing in the app to go back to. */}
      {user && (
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          sx={{ 
            margin: { xs: 1, sm: 2 },
            fontSize: { xs: "0.875rem", sm: "1rem" }
          }}
          onClick={() => navigate(-1)}
        >
          Volver
        </Button>
      )}
      <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, pt: user ? 0 : { xs: 2, sm: 3 } }}>
        {request ? (
          <>
            {request.response?.parentRadicadoId && (
              <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <RadicadoBacklink parentRadicadoId={request.response.parentRadicadoId} />
              </Box>
            )}
            
            {/* This page is also where the printed QR label leads, so an
                annulled radicado has to say so before anything else. */}
            {isCancelled && <FiledCancellationNotice filed={request} showActor={!!user} />}

            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ 
                    display: "flex", 
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between", 
                    mb: 2,
                    gap: { xs: 2, sm: 1 }
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                      Solicitud
                    </Typography>
                    <Box sx={{ 
                      display: "flex", 
                      gap: 1,
                      width: { xs: "100%", sm: "auto" },
                      flexDirection: { xs: "column", sm: "row" }
                    }}>
                      {canAddResponse && (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleAddResponse}
                          fullWidth={isMobile}
                          sx={{ 
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                            minWidth: { xs: "100%", sm: "auto" }
                          }}
                        >
                          Radicar respuesta
                        </Button>
                      )}
                      {canCancel && (
                        // Disabled with the reason rather than hidden: "why can't I
                        // annul this?" is the question the tooltip answers.
                        <Tooltip title={cancelDisabledReason || ""} disableHoverListener={!cancelDisabledReason}>
                          <span>
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => setOpenCancelDialog(true)}
                              disabled={!!cancelRefusal || !cancelAccess.allowed}
                              fullWidth={isMobile}
                              sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                            >
                              Anular
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {!isCancelled && user?.permissions.includes(PERMISSIONS.REQUESTSFILED.RESEND_EMAIL)
                        && <>
                          <IconButton
                            id="basic-button"
                            aria-controls={openMenu ? 'basic-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={openMenu ? 'true' : undefined}
                            onClick={(evt) => setAnchorEl(evt.currentTarget)}
                            sx={{ 
                              alignSelf: { xs: "flex-start", sm: "center" }
                            }}
                          >
                            <Mail />
                          </IconButton>
                          <Menu
                            id="basic-menu"
                            anchorEl={anchorEl}
                            open={openMenu}
                            onClose={() => setAnchorEl(null)}
                            MenuListProps={{
                              'aria-labelledby': 'basic-button',
                            }}
                          >
                            <MenuItem onClick={() => setConfirmInfo({
                              open: true,
                              title: "¿Deseas enviar nuevamente el comunicado a remitentes y destinatarios?",
                              anchor: "both"
                            })}>Enviar a Ambos</MenuItem>
                            <MenuItem onClick={() => setConfirmInfo({
                              open: true,
                              title: "¿Deseas enviar nuevamente el comunicado a remitentes?",
                              anchor: "senders"
                            })}>Enviar a Remitentes</MenuItem>
                            <MenuItem onClick={() => setConfirmInfo({
                              open: true,
                              title: "¿Deseas enviar nuevamente el comunicado a destinatarios?",
                              anchor: "recipients"
                            })}>Enviar a Destinatarios</MenuItem>
                          </Menu>
                        </>
                      }
                    </Box>
                  </Box>
                  <FiledViewItem item={request} />
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                Anexos
              </Typography>
              {!request.annexes || request.annexes.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                  No hay anexos
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {request.annexes.map((ann, idx) => (
                    <Link
                      key={idx}
                      href={ann.url}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ 
                        display: "block",
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        wordBreak: "break-word"
                      }}
                    >
                      {ann.name}
                    </Link>
                  ))}
                </Box>
              )}
            </Paper>

            {request.expedient && (
              <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                  Expediente
                </Typography>
                <Link 
                  component={RouterLink} 
                  to={`/expedients/${request.expedient.expedientTypeId}/${request.expedient.expedientId}`}
                  sx={{ 
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    wordBreak: "break-word"
                  }}
                >
                  {`${request.expedient.description} (${request.expedient.expedientId})`}
                </Link>
              </Paper>
            )}

            {/* Always show timeline - it will determine the root radicado automatically */}
            <Box sx={{ mb: { xs: 2, sm: 3 } }}>
              <RadicadoTimeline 
                rootRadicadoId={request.response?.rootRadicadoId || request.filedId}
                currentRadicadoId={request.filedId}
                currentRadicadoReferenceId={request.referenceId}
                isResponseView={!!request.response?.parentRadicadoId}
                originalRadicadoId={request.response?.rootRadicadoId || request.filedId}
                publicFiled={request}
              />
            </Box>
          </>
        ): (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
      </Container>
    </>
  );
}
